import { Reveal } from "@/components/common/Reveal";
import {
  BrowserChrome,
  FormMockup,
  ChatMockup,
  FinanceMockup,
} from "@/components/products/shared";

const PRODUCT_NAV = [
  { name: "XENI", status: "Live", hash: "#xeni", dotColor: "bg-emerald-500" },
  { name: "Robolab X", status: "Launching", hash: "#robolabx", dotColor: "bg-purple-500" },
  { name: "Metaroom", status: "Next", hash: "#metaroom", dotColor: "bg-amber-500" },
];

/* ── Shared cursor SVG ── */
function Cursor({ label, color, className }: { label: string; color: string; className?: string }) {
  return (
    <div className={`absolute z-10 pointer-events-none ${className ?? ""}`}>
      <svg className="w-3 h-4" viewBox="0 0 14 18" fill="none">
        <path d="M1 1L1 15L4.5 11.5L8 17L10 16L6.5 10L11 10L1 1Z" fill={color} stroke="white" strokeWidth="0.5" />
      </svg>
      <span
        className="absolute top-3.5 left-2.5 text-[6px] font-[family-name:var(--font-mono)] font-medium text-white px-1 py-px whitespace-nowrap"
        style={{ background: color }}
      >
        {label}
      </span>
    </div>
  );
}

/* ── WIP wireframe interiors ── */
function ConveyancingWireframe() {
  return (
    <div className="h-full p-2.5 text-[7px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
      {[
        { label: "Property address", filled: true },
        { label: "Title number", filled: true },
        { label: "Land Registry ref", filled: true },
        { label: "Seller details", filled: false },
      ].map((f) => (
        <div key={f.label} className="mb-1">
          <div className="text-[5px] mb-0.5 opacity-40">{f.label}</div>
          <div
            className="h-3 border rounded-sm"
            style={{
              borderColor: f.filled ? "rgb(16 185 129 / 0.2)" : "var(--color-border)",
              background: f.filled ? "rgb(16 185 129 / 0.03)" : "var(--color-fg)",
              opacity: f.filled ? 1 : 0.03,
            }}
          />
        </div>
      ))}
    </div>
  );
}

function AccountingWireframe() {
  return (
    <div className="h-full p-2.5 text-[7px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
      <div className="flex gap-1.5 mb-1 pb-0.5 border-b border-[var(--color-border)]">
        {["Acct", "Dr", "Cr"].map((h) => (
          <div key={h} className="flex-1 text-[5px] opacity-40 uppercase">{h}</div>
        ))}
      </div>
      {[true, true, false, false, false].map((filled, i) => (
        <div key={i} className="flex gap-1.5 mb-0.5">
          {[0, 1, 2].map((col) => (
            <div
              key={col}
              className="flex-1 h-2.5 rounded-sm"
              style={{
                background: filled ? "var(--color-fg)" : "var(--color-border)",
                opacity: filled ? 0.08 : 0.3,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function InsuranceWireframe() {
  return (
    <div className="h-full p-2.5 text-[7px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
      <div className="h-1.5 w-14 bg-[var(--color-fg)] opacity-[0.08] rounded-sm mb-2" />
      <div className="border border-[var(--color-border)] rounded-sm p-1.5 mb-1.5 h-6" />
      <div className="flex gap-1.5">
        <div className="flex-1 h-10 border border-dashed border-[var(--color-border)] rounded-sm" />
        <div className="flex-1 h-10 border border-dashed border-[var(--color-border)] rounded-sm opacity-40" />
      </div>
    </div>
  );
}

/* ── Card data for the marquee strip ── */
const MARQUEE_CARDS = [
  {
    name: "XENI",
    label: "QA",
    cursorColor: "#10b981",
    cursorClass: "agent-cursor-qa",
    opacity: 1,
    href: "#xeni",
    mockup: "form" as const,
  },
  {
    name: "Robolab X",
    label: "Safety",
    cursorColor: "#a78bfa",
    cursorClass: "agent-cursor-safety",
    opacity: 1,
    href: "#robolabx",
    mockup: "chat" as const,
  },
  {
    name: "Metaroom",
    label: "Analysis",
    cursorColor: "#f59e0b",
    cursorClass: "agent-cursor-analysis",
    opacity: 1,
    href: "#metaroom",
    mockup: "finance" as const,
  },
  {
    name: "Conveyancing",
    label: "Builder",
    cursorColor: "#06b6d4",
    cursorClass: "agent-cursor-builder-1",
    opacity: 0.7,
    href: undefined,
    mockup: "conveyancing" as const,
  },
  {
    name: "Accounting",
    label: "Designer",
    cursorColor: "#8b5cf6",
    cursorClass: "agent-cursor-designer-1",
    opacity: 0.5,
    href: undefined,
    mockup: "accounting" as const,
  },
  {
    name: "Insurance",
    label: "Builder",
    cursorColor: "#f97316",
    cursorClass: "agent-cursor-builder-2",
    opacity: 0.3,
    href: undefined,
    mockup: "insurance" as const,
  },
];

function MarqueeCard({ card }: { card: (typeof MARQUEE_CARDS)[number] }) {
  const inner = (
    <div className="relative w-[200px] flex-shrink-0 mx-3" style={{ opacity: card.opacity }}>
      <BrowserChrome height="h-[90px]">
        <div className="transform scale-[0.42] origin-top-left w-[238%] h-[238%] pointer-events-none">
          {card.mockup === "form" && <FormMockup />}
          {card.mockup === "chat" && <ChatMockup />}
          {card.mockup === "finance" && <FinanceMockup />}
          {card.mockup === "conveyancing" && <ConveyancingWireframe />}
          {card.mockup === "accounting" && <AccountingWireframe />}
          {card.mockup === "insurance" && <InsuranceWireframe />}
        </div>
      </BrowserChrome>
      <Cursor label={card.label} color={card.cursorColor} className={card.cursorClass} />
      <div className="text-[10px] font-[family-name:var(--font-mono)] text-center mt-1.5 text-[var(--color-fg-tertiary)]">
        {card.name}
      </div>
    </div>
  );

  if (card.href) {
    return (
      <a href={card.href} className="block hover:opacity-80 transition-opacity">
        {inner}
      </a>
    );
  }
  return inner;
}

export function AgentFactoryHero() {
  const cards = MARQUEE_CARDS;
  // Repeat enough times so one set is always wider than the viewport (each card ~226px, need > ~2000px)
  const strip = [...cards, ...cards, ...cards, ...cards];

  return (
    <section
      className="relative z-10 flex flex-col items-center pt-[var(--topnav-height)] pb-0 overflow-hidden"
    >
      {/* Title + subtitle + nav */}
      <div className="max-w-6xl mx-auto w-full text-center pt-16 mb-10 px-8">
        <Reveal>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            What we're building
          </h1>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-lg text-[var(--color-fg-secondary)] max-w-2xl mx-auto font-light mb-10">
            AI agents for industries where getting it wrong isn't an option.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <nav className="flex flex-wrap justify-center gap-4">
            {PRODUCT_NAV.map((p) => (
              <a
                key={p.name}
                href={p.hash}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] border-b border-[var(--color-border)] hover:border-[var(--color-fg-secondary)] transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${p.dotColor}`} />
                <span>{p.name}</span>
                <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] tracking-wider uppercase">{p.status}</span>
              </a>
            ))}
          </nav>
        </Reveal>
      </div>

      {/* Marquee strip */}
      <Reveal delay={300}>
        <div className="w-screen relative overflow-hidden mt-8 md:mt-12 hero-strip-mask">
          {/* Grid background behind pipeline cards */}
          <div className="absolute inset-0 assembly-grid opacity-[0.04] pointer-events-none" />

          <div className="flex items-start w-max animate-[marqueeLeft_60s_linear_infinite] hover:[animation-play-state:paused] py-8">
            {strip.map((card, i) => (
              <MarqueeCard key={`${card.name}-${i}`} card={card} />
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
